'use client';

import { TextPreview } from '@/components/ui/TextPreview';
import { documentSlug } from '@/lib/utils/documentMetadata';
import { Linkformat } from '@/lib/utils/markdownUtils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface HoverPreviewManagerProps {
  /** Container element to attach hover listeners to */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Locale for preview content */
  locale: string;
  /** Whether the manager is enabled */
  enabled?: boolean;
  /** CSS selector for previewable links (default: all non-fragment links) */
  linkSelector?: string;
}

interface ActiveLinkState {
  /** The link element being hovered */
  element: HTMLAnchorElement;
  /** Transformed href for the preview */
  href: string;
  /** Position for the popup */
  position: { top: number; left: number };
  /** Counter to reset scroll position when link changes */
  resetCounter: number;
}

const POPUP_WIDTH = 550;
const POPUP_HEIGHT = 450;
const PADDING = 10;
const HIDE_DELAY = 200;

/**
 * Calculate optimal popup position based on available viewport space.
 * Prefers: below > right > left > above > fallback below
 */
function calculatePopupPosition(rect: DOMRect): { top: number; left: number } {
  const spaceBelow = window.innerHeight - rect.bottom - PADDING;
  const spaceAbove = rect.top - PADDING;
  const spaceRight = window.innerWidth - rect.right - PADDING;
  const spaceLeft = rect.left - PADDING;

  let top: number;
  let left: number;

  // Try below first
  if (spaceBelow >= POPUP_HEIGHT) {
    top = rect.bottom + PADDING;
    left = rect.left + rect.width / 2 - POPUP_WIDTH / 2;
  }
  // Try right side
  else if (spaceRight >= POPUP_WIDTH) {
    left = rect.right + PADDING;
    top = rect.top + rect.height / 2 - POPUP_HEIGHT / 2;
  }
  // Try left side
  else if (spaceLeft >= POPUP_WIDTH) {
    left = rect.left - POPUP_WIDTH - PADDING;
    top = rect.top + rect.height / 2 - POPUP_HEIGHT / 2;
  }
  // Try above
  else if (spaceAbove >= POPUP_HEIGHT) {
    top = rect.top - POPUP_HEIGHT - PADDING;
    left = rect.left + rect.width / 2 - POPUP_WIDTH / 2;
  }
  // Fallback: place below
  else {
    top = rect.bottom + PADDING;
    left = rect.left + rect.width / 2 - POPUP_WIDTH / 2;
  }

  // Clamp horizontal position to viewport
  if (left < PADDING) {
    left = PADDING;
  }
  if (left + POPUP_WIDTH > window.innerWidth - PADDING) {
    left = window.innerWidth - POPUP_WIDTH - PADDING;
  }

  // Clamp vertical position to viewport
  if (top < PADDING) {
    top = PADDING;
  }
  if (top + POPUP_HEIGHT > window.innerHeight - PADDING) {
    top = window.innerHeight - POPUP_HEIGHT - PADDING;
  }

  return { top, left };
}

/**
 * HoverPreviewManager - Manages hover preview popups for document links.
 *
 * Matches all non-fragment links by default. The preview tier is automatic:
 *   - Internal legislation/decision → full document preview with section highlighting
 *   - External slov-lex.sk → transformed to internal, full preview
 *   - External website → lightweight card (favicon + domain + URL + copy)
 *   - Unknown/broken → no popup shown
 *
 * Uses React Portal for proper lifecycle management and event delegation
 * for efficient handling of dynamically rendered links.
 */
export function HoverPreviewManager({
  containerRef,
  locale,
  enabled = true,
  linkSelector = 'a:not([href^="#"])',
}: HoverPreviewManagerProps) {
  const [activeLink, setActiveLink] = useState<ActiveLinkState | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resetCounterRef = useRef(0);
  // Tracks whether a dropdown inside TextPreview is open.
  // While true, suppress popup hide so the user can click dropdown items.
  const dropdownOpenRef = useRef(false);

  // Transform href using Linkformat (handles slov-lex → Lexomat, /SK/ → full URL,
  // numeric IDs, etc.) with a fallback for relative ../ paths.
  const transformHref = useCallback(
    (href: string): string | null => {
      // Skip non-previewable schemes
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return null;

      // Relative ../ paths: resolve against current location first
      if (href.startsWith('../')) {
        try {
          const currentPath = window.location.pathname;
          const resolved = new URL(href, window.location.origin + currentPath);
          const resolvedPath = resolved.pathname;
          const hash = resolved.hash;
          const skMatch = resolvedPath.match(/(\/SK\/.*)$/);
          if (skMatch) {
            // Use locale-aware slug so /en/... users get /en/document/... not /en/dokument/...
            return `/${locale}/${documentSlug(locale)}${skMatch[1]}${hash}`;
          }
        } catch {
          // invalid URL, fall through
        }
      }

      // Linkformat handles: /SK/..., slov-lex.sk URLs, numeric IDs, full URLs
      const formatted = Linkformat(href, locale);
      return formatted || null;
    },
    [locale]
  );

  // Clear any pending hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Schedule hiding the popup — suppressed while a dropdown inside the popup is open
  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    if (dropdownOpenRef.current) return;
    hideTimeoutRef.current = setTimeout(() => {
      setActiveLink(null);
    }, HIDE_DELAY);
  }, [clearHideTimeout]);

  // Called by TextPreview when its copy/link dropdown opens or closes
  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      dropdownOpenRef.current = open;
      if (!open) {
        // Dropdown just closed — schedule hide in case mouse has already left
        scheduleHide();
      }
    },
    [scheduleHide]
  );

  // Show preview for a link
  const showPreview = useCallback(
    (link: HTMLAnchorElement) => {
      const rawHref = link.getAttribute('href');
      if (!rawHref) return;

      const href = transformHref(rawHref);
      if (!href) return; // non-previewable (mailto:, tel:, etc.)

      clearHideTimeout();

      const rect = link.getBoundingClientRect();
      const position = calculatePopupPosition(rect);

      resetCounterRef.current += 1;

      setActiveLink({
        element: link,
        href,
        position,
        resetCounter: resetCounterRef.current,
      });
    },
    [transformHref, clearHideTimeout]
  );

  // Handle popup mouse enter - cancel hide
  const handlePopupMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  // Handle popup mouse leave - schedule hide
  const handlePopupMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  // Attach event delegation listeners to container
  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest(linkSelector) as HTMLAnchorElement | null;
      if (link) {
        showPreview(link);
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const link = (e.target as HTMLElement).closest(
        linkSelector
      ) as HTMLAnchorElement | null;
      // Only hide if we're leaving the link entirely (not moving to a child element)
      if (link && (!relatedTarget || !link.contains(relatedTarget))) {
        scheduleHide();
      }
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      clearHideTimeout();
    };
  }, [
    enabled,
    containerRef,
    linkSelector,
    showPreview,
    scheduleHide,
    clearHideTimeout,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  // Don't render anything if disabled or no active link
  if (!enabled || !activeLink) {
    return null;
  }

  // Use React Portal - React manages the full lifecycle, no race conditions
  return createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'auto',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow:
          '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        top: `${activeLink.position.top}px`,
        left: `${activeLink.position.left}px`,
        width: `${POPUP_WIDTH}px`,
        height: `${POPUP_HEIGHT}px`,
      }}
      onMouseEnter={handlePopupMouseEnter}
      onMouseLeave={handlePopupMouseLeave}
    >
      <TextPreview
        href={activeLink.href}
        locale={locale}
        width={POPUP_WIDTH}
        height={POPUP_HEIGHT}
        highlightMode='hover'
        resetScrollTrigger={activeLink.resetCounter}
        onCopyLinkOpenChange={handleDropdownOpenChange}
        onLinkOptionsOpenChange={handleDropdownOpenChange}
      />
    </div>,
    document.body
  );
}
